import { useEffect, useRef } from "react";
import Button from "@/components/Button";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";
import type { InteractionTopicInput } from "../queries";
import { isTopicLocked, type TopicValidationErrors } from "../_utils/topicsValidation";

type TopicsEditorProps = {
  topics: InteractionTopicInput[];
  errors: TopicValidationErrors;
  focusKey: string | null;
  onFocusApplied: () => void;
  onChangeTopicName: (topicIndex: number, name: string) => void;
  onChangeSubtopics: (topicIndex: number, text: string) => void;
  onAddTopic: () => void;
  onRemoveTopic: (topicIndex: number) => void;
};

export function topicFocusKey(topicIndex: number) {
  return `topic:${topicIndex}`;
}

function subtopicRows(lineCount: number): number {
  return Math.min(Math.max(lineCount + 1, 3), 12);
}

export default function TopicsEditor({
  topics,
  errors,
  focusKey,
  onFocusApplied,
  onChangeTopicName,
  onChangeSubtopics,
  onAddTopic,
  onRemoveTopic
}: TopicsEditorProps) {
  const inputRefs = useRef(new Map<string, HTMLInputElement>());

  useEffect(() => {
    if (!focusKey) return;
    const input = inputRefs.current.get(focusKey);
    if (input) {
      input.focus();
      input.select();
    }
    onFocusApplied();
  }, [focusKey, onFocusApplied, topics]);

  const setRef = (key: string) => (element: HTMLInputElement | null) => {
    if (element) {
      inputRefs.current.set(key, element);
    } else {
      inputRefs.current.delete(key);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {errors.formError ? (
        <p role="alert" data-testid="topics-form-error" className="text-body-medium text-sys-brand-error">
          {errors.formError}
        </p>
      ) : null}
      <div
        data-testid="topics-grid"
        className="grid grid-cols-1 items-start gap-x-8 gap-y-6 md:grid-cols-2 xl:grid-cols-3"
      >
        {topics.map((topic, topicIndex) => {
          // Locked Other rows render as static footer lines (below), never as
          // disabled inputs — state indexes are preserved for keys/handlers.
          if (isTopicLocked(topics, topicIndex)) return null;
          const topicError = errors.topicErrors[topicIndex];
          const subtopicError = errors.subtopicErrors[topicIndex];
          // State holds the raw textarea lines; trimming and the canonical
          // Other happen at save serialization, never while typing.
          const lines = topic.subtopics;
          const hasSubtopics = lines.some((line) => line.trim().length > 0);
          return (
            <div
              key={topicIndex}
              data-testid={`topic-card-${topicIndex}`}
              className="flex flex-col gap-6 rounded-lg bg-sys-brand-surface-container-low p-4"
            >
              <TextField
                ref={setRef(topicFocusKey(topicIndex))}
                label="Topic"
                value={topic.name}
                onChangeText={(value) => onChangeTopicName(topicIndex, value)}
                error={Boolean(topicError)}
                errorText={topicError}
                trailingIcon={
                  <IconButton
                    name="delete"
                    skipMargin
                    ariaLabel={`Remove ${topic.name || "topic"}`}
                    onClick={() => onRemoveTopic(topicIndex)}
                  />
                }
                className="flex-1"
              />
              <div className="flex flex-col gap-6">
                <TextField
                  label="Subtopics"
                  placeholder="One subtopic per line"
                  type="textarea"
                  rows={subtopicRows(lines.length)}
                  value={lines.join("\n")}
                  onChangeText={(value) => onChangeSubtopics(topicIndex, value)}
                  error={Boolean(subtopicError)}
                  errorText={subtopicError}
                  className="flex-1"
                />
                {hasSubtopics ? (
                  <p className="text-body-small text-sys-brand-on-surface-variant">+ Subtopic: Other [always shown]</p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-body-small text-sys-brand-on-surface-variant" data-testid="topics-other-footer">
        + Topic: Other [always shown]
      </p>
      <div>
        <Button variant="outlined" icon="add" onClick={onAddTopic} testId="add-topic">
          Add topic
        </Button>
      </div>
    </div>
  );
}
